const ISO_DATE_PREFIX = /^(\d{4})-(\d{1,2})-(\d{1,2})(.*)$/;
const YMD_SLASH_PREFIX = /^(\d{4})\/(\d{1,2})\/(\d{1,2})(.*)$/;
const DMY_PREFIX = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})(.*)$/;

function isValidDateParts(year, month, day) {
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day;
}

function formatSuffix(suffix) {
  if (!suffix) return "";
  const time = suffix.match(/[T ](\d{2}:\d{2}(?::\d{2})?)/);
  return time ? ` ${time[1]}` : "";
}

export function formatDateValue(value) {
  if (value === null || value === undefined || value === "") return "";
  const text = String(value).trim();
  if (!text || text === "0000-00-00") return text;

  const iso = text.match(ISO_DATE_PREFIX);
  if (iso) {
    const [, yearText, monthText, dayText, suffix] = iso;
    const year = Number(yearText);
    const month = Number(monthText);
    const day = Number(dayText);
    if (!isValidDateParts(year, month, day)) return text;
    return `${String(day).padStart(2, "0")}-${String(month).padStart(2, "0")}-${yearText}${formatSuffix(suffix)}`;
  }

  const slash = text.match(YMD_SLASH_PREFIX);
  if (slash) {
    const [, yearText, monthText, dayText, suffix] = slash;
    const year = Number(yearText);
    const month = Number(monthText);
    const day = Number(dayText);
    if (!isValidDateParts(year, month, day)) return text;
    return `${String(day).padStart(2, "0")}-${String(month).padStart(2, "0")}-${yearText}${formatSuffix(suffix)}`;
  }

  const dmy = text.match(DMY_PREFIX);
  if (dmy) {
    const [, dayText, monthText, yearText, suffix] = dmy;
    const day = Number(dayText);
    const month = Number(monthText);
    const year = Number(yearText);
    if (!isValidDateParts(year, month, day)) return text;
    return `${String(day).padStart(2, "0")}-${String(month).padStart(2, "0")}-${yearText}${formatSuffix(suffix)}`;
  }

  return text;
}

export function isReportDateColumn(columnName = "") {
  const column = String(columnName).toLowerCase().replace(/[^a-z0-9]/g, "");
  return column.includes("date") || column.includes("datetime")
    || column.includes("timestamp")
    || /(?:created|updated|posted|deleted|started|finished|uploaded|lastsuccess)at$/.test(column);
}

export function formatReportValue(value, columnName = "") {
  if (value === null || value === undefined || value === "") return "";
  const text = String(value).trim();
  const formatted = formatDateValue(value);
  if (formatted !== text) return formatted;
  if (!isReportDateColumn(columnName)) return value;

  const compact = text.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (compact) {
    const [, yearText, monthText, dayText] = compact;
    if (isValidDateParts(Number(yearText), Number(monthText), Number(dayText))) {
      return `${dayText}-${monthText}-${yearText}`;
    }
  }
  return value;
}

export function formatDateTime(value) {
  if (value === null || value === undefined || value === "") return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return formatDateValue(value);
  const parts = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date).reduce((result, part) => {
    result[part.type] = part.value;
    return result;
  }, {});
  return `${parts.day}-${parts.month}-${parts.year} ${parts.hour}:${parts.minute}`;
}

export function toIsoDate(value) {
  if (value === null || value === undefined || value === "") return "";
  const text = String(value).trim();
  let year;
  let month;
  let day;

  const iso = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) {
    [, year, month, day] = iso;
  } else {
    const dmy = text.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
    if (!dmy) return "";
    [, day, month, year] = dmy;
  }

  const y = Number(year);
  const m = Number(month);
  const d = Number(day);
  if (!isValidDateParts(y, m, d)) return "";
  return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}
